import { useCallback } from 'react';

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import {
  BiomassDistributionBinSize,
  BiomassDistributionInterval,
  BiomassDistributionView,
} from '@/components/biomassDistribution/biomassDistribution.types.ts';

import { storeNames } from './config/config.type.ts';

/** Per-widget display preferences. Session-scoped (not synced to the server). */
export interface BiomassDistributionPreferences {
  interval: BiomassDistributionInterval;
  binSize: BiomassDistributionBinSize;
  view: BiomassDistributionView;
  estimatedCurveVisible: boolean;
  normalCurveVisible: boolean;
}

const defaultBiomassDistributionPreferences: BiomassDistributionPreferences = {
  interval: BiomassDistributionInterval.Week,
  binSize: BiomassDistributionBinSize.SmallMedium,
  view: BiomassDistributionView.Chart,
  estimatedCurveVisible: false,
  normalCurveVisible: false,
};

interface BiomassDistributionPreferencesStore {
  preferencesByWidgetId: Record<string, Partial<BiomassDistributionPreferences>>;
  setPreference: <K extends keyof BiomassDistributionPreferences>(
    widgetId: string,
    key: K,
    value: BiomassDistributionPreferences[K]
  ) => void;
}

const useBiomassDistributionPreferencesZustandStore = create(
  persist<BiomassDistributionPreferencesStore>(
    set => ({
      preferencesByWidgetId: {},
      setPreference: (widgetId, key, value) =>
        set(state => {
          // Skip no-op updates so subscribers are not notified with a new reference.
          if (state.preferencesByWidgetId[widgetId]?.[key] === value) return state;
          return {
            preferencesByWidgetId: {
              ...state.preferencesByWidgetId,
              [widgetId]: { ...state.preferencesByWidgetId[widgetId], [key]: value },
            },
          };
        }),
    }),
    {
      name: storeNames.biomassDistribution,
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);

type UseBiomassDistributionPreferencesStorageResult = BiomassDistributionPreferences & {
  setInterval: (value: BiomassDistributionInterval) => void;
  setBinSize: (value: BiomassDistributionBinSize) => void;
  setView: (value: BiomassDistributionView) => void;
  setEstimatedCurveVisible: (value: boolean) => void;
  setNormalCurveVisible: (value: boolean) => void;
};

/**
 * Reads and writes a single widget's display preferences, keyed by `widgetId`.
 * Falls back to `defaults` for any preference not yet stored.
 */
export const useBiomassDistributionPreferencesStorage = (
  widgetId: string,
  defaults: BiomassDistributionPreferences = defaultBiomassDistributionPreferences
): UseBiomassDistributionPreferencesStorageResult => {
  const { stored, setPreference } = useBiomassDistributionPreferencesZustandStore(
    useShallow(s => ({
      stored: s.preferencesByWidgetId[widgetId],
      setPreference: s.setPreference,
    }))
  );

  const setInterval = useCallback(
    (value: BiomassDistributionInterval) => setPreference(widgetId, 'interval', value),
    [setPreference, widgetId]
  );
  const setBinSize = useCallback(
    (value: BiomassDistributionBinSize) => setPreference(widgetId, 'binSize', value),
    [setPreference, widgetId]
  );
  const setView = useCallback(
    (value: BiomassDistributionView) => setPreference(widgetId, 'view', value),
    [setPreference, widgetId]
  );
  const setEstimatedCurveVisible = useCallback(
    (value: boolean) => setPreference(widgetId, 'estimatedCurveVisible', value),
    [setPreference, widgetId]
  );
  const setNormalCurveVisible = useCallback(
    (value: boolean) => setPreference(widgetId, 'normalCurveVisible', value),
    [setPreference, widgetId]
  );

  return {
    ...defaults,
    ...stored,
    setInterval,
    setBinSize,
    setView,
    setEstimatedCurveVisible,
    setNormalCurveVisible,
  };
};
